/**
 * Tunable constants for vehicle marker clustering.
 *
 * Defaults exported here are the FACTORY values. The user can override them
 * from Settings → "Agrupamiento de vehículos" (persisted in useSettings).
 *
 * Pixel-based: the radius is measured in screen pixels at the current zoom,
 * so the perceived "closeness" of a cluster is consistent regardless of zoom
 * level — at street zoom 3 vehicles in the same parking lot will cluster, at
 * regional zoom 3 vehicles in the same neighborhood will cluster.
 */

/**
 * Zoom anchors for radius computation.
 *
 *   z ≤ 8   →  80 px   (regional view, matches leaflet.markercluster default)
 *   z = 12  →  70 px   (city / neighborhood)
 *   z = 16  →  65 px   (street)
 *   z ≥ 19  →  60 px   (street / block — keep clusters attractive in deep
 *                       zooms where vehicles still look adjacent to the user
 *                       but pixel distance grows quickly between zoom levels)
 *
 * Linear interpolation between anchors. The final radius is multiplied by
 * `sensitivity` (1.0 = factory; higher = more aggressive grouping). Curve is
 * monotone non-increasing.
 *
 * Calibrated against industry standards:
 *   - leaflet.markercluster default: 80px constant
 *   - Mapbox MarkerClusterer: 50–60px effective at typical zooms
 *   - Google MarkerClusterer: ~60px
 */
const RADIUS_POINTS = [
  [8, 80],
  [12, 70],
  [16, 65],
  [19, 60],
];

function baseRadius(zoom) {
  if (zoom == null || Number.isNaN(zoom)) return RADIUS_POINTS[2][1];
  if (zoom <= RADIUS_POINTS[0][0]) return RADIUS_POINTS[0][1];
  if (zoom >= RADIUS_POINTS.at(-1)[0]) return RADIUS_POINTS.at(-1)[1];
  for (let i = 0; i < RADIUS_POINTS.length - 1; i++) {
    const [z0, r0] = RADIUS_POINTS[i];
    const [z1, r1] = RADIUS_POINTS[i + 1];
    if (zoom >= z0 && zoom <= z1) {
      const t = (zoom - z0) / (z1 - z0);
      return r0 + (r1 - r0) * t;
    }
  }
  return RADIUS_POINTS[2][1];
}

/**
 * Universal zoom thresholds — same for all presets.
 *
 * `disableAboveZoom`: clustering stays active up to and including this zoom,
 * and disables only above it. With `TILE_MAX_ZOOM = 21`, a value of 20 means
 * only the deepest level (21) renders every vehicle individually. The
 * effective check in the layer is `currentZoom > disableAboveZoom`.
 *
 * `groupAllBelowZoom`: at city/region zoom and below, group everything
 * including moving vehicles — individual markers are unreadable at that
 * scale.
 */
const UNIVERSAL_THRESHOLDS = Object.freeze({
  disableAboveZoom: 20,
  groupAllBelowZoom: 12,
});

/**
 * Vehicle clustering presets. Each preset maps to a fixed
 * (sensitivity, minSize) combo. Zoom thresholds are universal across
 * presets (see UNIVERSAL_THRESHOLDS).
 *
 * Calibration goal: each preset must FEEL different from the next when
 * toggled. Earlier values (0.7 / 1.0 / 1.5) were too close to each other —
 * users could not visually distinguish "detalle" from "operacion" in
 * normal city zooms.
 *
 * Per-row rationale:
 *
 *   detalle      — { sensitivity: 0.4, minSize: 4 }
 *                  Very small radius (0.4×) + minSize 4 → effectively no
 *                  clustering unless 4+ vehicles are physically stacked on
 *                  the same point. Every vehicle is visible individually.
 *                  Emphasis: live tracking and per-vehicle inspection.
 *
 *   operacion    — { sensitivity: 1.2, minSize: 3 }
 *                  Slightly above factory radius (1.2×) with minSize 3.
 *                  Calibrated so a typical "4 motos in the same parking
 *                  lot" scenario clusters at street zoom (19–20) without
 *                  needing to switch to vistaGlobal. Recommended baseline.
 *
 *   vistaGlobal  — { sensitivity: 2.0, minSize: 2 }
 *                  Very large radius (2.0×) + minSize 2 → any pair within
 *                  the same neighborhood forms a group. Emphasis: see the
 *                  whole fleet at a glance, even at city/regional zooms.
 */
export const CLUSTER_PRESETS = Object.freeze({
  detalle: { sensitivity: 0.4, minSize: 4 },
  operacion: { sensitivity: 1.2, minSize: 3 },
  vistaGlobal: { sensitivity: 2.0, minSize: 2 },
});

/** Default preset for new users and fallback for invalid keys. */
export const CLUSTER_PRESET_DEFAULT = "operacion";

/**
 * Resolve a preset key to the full parameter tuple consumed by
 * VehicleClusterLayer. Unknown / missing keys fall back to the default.
 *
 * @param {string | null | undefined} presetKey
 * @returns {{ sensitivity: number, minSize: number, groupAllBelowZoom: number, disableAboveZoom: number }}
 */
export function resolveClusterPreset(presetKey) {
  const preset =
    CLUSTER_PRESETS[presetKey] ?? CLUSTER_PRESETS[CLUSTER_PRESET_DEFAULT];
  return {
    sensitivity: preset.sensitivity,
    minSize: preset.minSize,
    ...UNIVERSAL_THRESHOLDS,
  };
}

/**
 * @param {number} zoom         Current map zoom.
 * @param {number} [sensitivity=1.0]  Multiplier — 0.5x = mitad de radio (menos agresivo),
 *                                    2x = doble radio (más agresivo).
 * @returns {number} Radius in screen pixels.
 */
export function getClusterRadiusPx(zoom, sensitivity = 1.0) {
  const m = Number.isFinite(sensitivity) && sensitivity > 0 ? sensitivity : 1;
  return Math.round(baseRadius(zoom) * m);
}
